import type {EditionSummary} from '../../types/types.ts';
import {escapeHtml} from '../../src/utils/html.ts';
import {t} from '../lib/i18n.ts';
import {Article, type ArticleData} from './article.ts';
import {ItemState} from './item-state.ts';

/**
 * Creates a synthetic Article from an AI-generated summary.
 * This allows the summary to flow through the existing article pipeline,
 * getting pressflow layout, TOC entry, and all standard article behaviors.
 */
export function createSummaryArticle(summary: EditionSummary, editionId: string, publishDate: string): Article {
	const html = formatSummaryAsHtml(summary.content || '');
	const summaryId = `summary-${editionId}`;

	// Use end of day to ensure summary sorts first within the day (desc sort by time)
	const endOfDay = new Date(publishDate);
	endOfDay.setHours(23, 59, 59, 999);

	const data: ArticleData = {
		id: summaryId,
		objectId: null,
		data: {category: 'summary', articletype: 'summary'},
		publishedAt: endOfDay.toISOString(),
		sort: -1, // Before all other articles
		groupBy: t('todaysEdition'),
		section: '',
		layout: {html},
		pretitle: '',
		title: t('todaysEdition'),
		intro: '',
		size: html.length
	};

	const state = new ItemState({
		id: summaryId,
		state: undefined,
		starred: false,
		mobileProgress: 0,
		desktopProgress: 0
	});

	return new Article(data, state);
}

function formatSummaryAsHtml(content: string): string {
	const paragraphs = content
		.split('\n\n')
		.filter((p) => p.trim())
		.map((p) => `<p>${escapeHtml(p.trim())}</p>`)
		.join('');

	// Use inline styles to guarantee text-align overrides any column effects
	const disclaimer = `<div class="fact-box"><div class="fact-box-body" style="text-align: left;"><p style="text-align: left; margin: 0;"><svg style="vertical-align: middle; margin-right: 0.4em;" viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/><path d="M19 5l1 3 1-3-1-3-1 3z"/><path d="M5 17l.7 2 .7-2-.7-2-.7 2z"/></svg>${t('summaryDisclaimer')}</p></div></div>`;

	// Wrap in article-body structure expected by the rendering pipeline
	return `<article class="article-body"><div class="body">${paragraphs}${disclaimer}</div></article>`;
}
